import React, { useState, useCallback } from 'react';
import { useBoolean } from 'react-hanger';
import { useQuery } from '@apollo/client/react';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import {
    GridHeaderSelectionChangeEvent,
    GridSelectionChangeEvent,
    GridFilterChangeEvent,
} from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import style from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/style.module.scss';
import { IExtendedLibraryControlFields } from '@pages-clean/risk-assessment/risk-log/risk-log.interface';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import type { getNamedItemsByType } from '@queries/generated/common.generated';

interface AssignControlsToRiskDialogProps {
    riskStatement: string | null | undefined;
    assignableControls: IExtendedLibraryControlFields[];
    assigning: boolean;
    onClose: () => void;
    onSubmit: (studyControlIds: string[], libraryControlIds: string[], closeDialog: boolean) => void;
    onAddControlOpen: () => void;
}

type SelectState = SelectDescriptor;

const AssignControlsToRiskDialog = ({
    riskStatement,
    assignableControls,
    assigning,
    onClose,
    onSubmit,
    onAddControlOpen,
}: AssignControlsToRiskDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.dialogs.assignControls' });
    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();

    const {
        value: clearSelectedRows,
        setTrue: setClearSelectedRowsTrue,
        setFalse: setClearSelectedRowsFalse,
    } = useBoolean(false);

    const [select, setSelect] = useState<SelectState>({});
    const [filterState, setFilterState] = useState<CompositeFilterDescriptor>({
        logic: 'and',
        filters: [],
    });

    const { data: controlPhaseData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROL_PHASE },
    });

    const { data: controlTypeData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CONTROL_TYPE },
    });

    const onSelectionChange = useCallback(
        (event: GridSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const onHeaderSelectionChange = useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            setClearSelectedRowsFalse();
            if (event.select) setSelect(event.select);
        },
        [setClearSelectedRowsFalse],
    );

    const onSubmitAssign = (close: boolean) => {
        if (!assignableControls.length) return;

        const selectedRowKeys = Object.keys(select).filter((k) => select[k] === true);
        if (!selectedRowKeys.length) return;

        const selectedStudyControlIds = assignableControls
            .filter((c) => selectedRowKeys.includes(c.id) && !c.library)
            .map((c) => c.id);
        const selectedLibraryControlIds = assignableControls
            .filter((c) => selectedRowKeys.includes(c.id) && c.library)
            .map((c) => c.id);

        setSelect({});
        setClearSelectedRowsTrue();
        onSubmit(selectedStudyControlIds, selectedLibraryControlIds, close);
    };

    const selectedCount = Object.keys(select).filter((k) => select[k] === true).length;
    const formActionsEnabled = selectedCount > 0 && !assigning;
    const newDisabled = selectedCount > 0 || assigning;
    const statement = riskStatement ?? '';
    const dialogTitle = `${t('title')}`;

    const onCloseHandler = (event?: unknown) => {
        const action = (event as { action?: string } | undefined)?.action;
        if (action === 'button') {
            if (newDisabled) return;
            setClearSelectedRowsTrue();
            setSelect({});
            onAddControlOpen();
        } else {
            onClose();
        }
    };

    const columns: GridColumnProps[] = [
        {
            id: 'checkbox',
            field: 'checkbox',
            columnType: 'checkbox',
            className: 'checkboxSelection',
            width: 40,
            customColumn: true,
            resizable: false,
            locked: true,
            filterable: false,
        },
        {
            id: 'controlPhase',
            field: 'controlPhase',
            title: t('grid.columns.phase'),
            lockWidth: true,
            minWidth: 120,
            filter: 'text',
            filterKey: 'name',
            lovData: controlPhaseData?.namedItemsByType
                ? controlPhaseData.namedItemsByType
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (n: INamedItem) => n?.active === false,
                    renderValue: (n: INamedItem) => n?.name,
                }),
            },
        },
        {
            id: 'type',
            field: 'type',
            title: t('grid.columns.type'),
            lockWidth: true,
            minWidth: 120,
            filter: 'text',
            filterKey: 'name',
            lovData: controlTypeData?.namedItemsByType
                ? controlTypeData.namedItemsByType
                      .map((item) => item?.name as string)
                      .sort((a, b) => {
                          return a.localeCompare(b);
                      })
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (n: INamedItem) => n?.active === false,
                    renderValue: (n: INamedItem) => n?.name,
                }),
            },
        },
        {
            id: 'description',
            field: 'description',
            title: t('grid.columns.description'),
            customColumn: true,
            filter: 'text',
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            id: 'library',
            field: 'library',
            title: t('grid.columns.library'),
            cells: { data: YesNoCell },
            lockWidth: true,
            width: 95,
            filter: 'boolean',
        },
        {
            id: 'linked',
            field: 'linked',
            title: t('grid.columns.linked'),
            cells: { data: YesNoCell },
            lockWidth: true,
            width: 95,
            filter: 'boolean',
        },
    ];

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="assign-controls-to-risk-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSubmitAssign(false)}
            primaryBtnLabel={t('buttons.assign')}
            primaryBtnDisabled={!formActionsEnabled}
            secondaryBtnLabel={t('buttons.new')}
            secondaryBtnDisabled={newDisabled}
            onAdditionalAction={() => onSubmitAssign(true)}
            additionalBtnLabel={t('buttons.assignAndClose')}
            additionalBtnDisabled={!formActionsEnabled}
            initialHeight={520}
            initialWidth={800}
            height={null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="k-w-full k-h-full">
                <div>
                    <InfoField type="Info" message={t('infoMessage')} />
                </div>
                <div>
                    {statement.length <= 600 ? (
                        <>
                            <span className="k-font-medium">{t('riskStatement')} - </span>
                            <span style={{ wordBreak: 'break-word' }}>{statement}</span>
                        </>
                    ) : (
                        <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                            <span className="k-font-medium">{t('riskStatement')} - </span>
                            <span
                                style={{ wordBreak: 'break-word' }}
                                title={statement}
                            >{`${statement.slice(0, 600)}...`}</span>
                        </Tooltip>
                    )}
                </div>
                <div style={{ height: '300px', paddingBottom: '0.25rem' }}>
                    <Grid
                        excludeHiddenRowMenuActions
                        title={t('description')}
                        maxHeight="auto"
                        testDataId="assign-controls-grid"
                        data={assignableControls ?? []}
                        columns={columns}
                        filterable
                        loading={assigning}
                        useLoadingOverlay
                        showAddIcon={false}
                        scrollable="virtual"
                        dataItemKey="id"
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: 'multiple',
                        }}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                        onSelectionChange={onSelectionChange}
                        clearGridSelectedRows={clearSelectedRows}
                        useCardWrapper={false}
                        filter={filterState}
                        onFilterChange={async (e: GridFilterChangeEvent) => {
                            const next = (e.filter || { logic: 'and', filters: [] }) as CompositeFilterDescriptor;
                            setFilterState(next);
                            setClearSelectedRowsTrue();
                            setSelect({});
                            return true;
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AssignControlsToRiskDialog;
